import type { Query, QueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

import type { ProjectPlan, ProjectRecord } from "@/types/api";

import { putAsset } from "./db";
import { createSemaphore, ensureAssetCached, primeObjectUrl } from "./blobs";
import {
  type AssetDownload,
  planDownloadsFrom,
  recordDownloadsFrom,
  thumbAssetKey,
} from "./asset-keys";
import { isOnline } from "./online-status";
import { isPinnedSync } from "./pins";

/**
 * Continuous backfill for the offline asset cache. See [[offline-asset-cache]].
 *
 * A single QueryCache subscriber: whenever a pinned project's record or plan
 * data lands in the cache — a mutation refetch, a resync, an optimistic
 * update, or persister hydration — it ensures every image that data references
 * is cached, so the project stays fully renderable offline without waiting for
 * the next reconnect.
 */

/** Debounce window for coalescing a burst of query-cache events per project. */
const DEBOUNCE_MS = 400;
const BACKFILL_CONCURRENCY = 4;

/**
 * Pull `results` out of a TanStack infinite-query `data` payload
 * (`{ pages: { results: T[] }[] }`), tolerant of any other shape.
 */
function flattenInfiniteResults<T>(data: unknown): T[] {
  if (!data || typeof data !== "object") return [];
  const pages = (data as { pages?: unknown }).pages;
  if (!Array.isArray(pages)) return [];
  const out: T[] = [];
  for (const page of pages) {
    const results = (page as { results?: unknown })?.results;
    if (Array.isArray(results)) out.push(...(results as T[]));
  }
  return out;
}

/**
 * Seed an offline-created record's photo into the asset cache from the local
 * capture bytes the device still holds, marked **provisional**.
 *
 * Lets the photo render offline the instant its outbox upload succeeds — even
 * if connectivity drops before any download could run. The backfill subscriber
 * later upgrades the provisional blob to the durable server thumbnail. See
 * [[offline-asset-cache]]. Best-effort and self-contained — it never throws,
 * so a failure cannot derail the outbox drain's success handler.
 */
export async function seedProvisionalThumbnail(
  record: ProjectRecord,
  projectId: string,
  localBlob: Blob,
): Promise<void> {
  return seedProvisionalAsset(thumbAssetKey(record), projectId, localBlob);
}

/**
 * The same provisional seed against an explicit asset key, for callers that
 * know which single image they are seeding rather than a record's cover.
 *
 * Used by the per-photo markup re-edit ([[image-viewer-modal]]): a re-edit
 * replaces one photo's bytes in place, which advances the record's
 * `updated_at` and therefore that photo's content-versioned key. The refetched
 * tile is then a guaranteed cache miss against a derivative the server may
 * still be regenerating — so the freshly-rendered thumbnail is seeded under the
 * POST-edit key instead. See [[offline-asset-cache]].
 *
 * Best-effort and self-contained — it never throws, so a failure cannot derail
 * a mutation's success handler.
 */
export async function seedProvisionalAsset(
  key: string,
  projectId: string,
  localBlob: Blob,
): Promise<void> {
  try {
    // The asset cache only holds data for pinned (offline-capable) projects.
    if (!key || !isPinnedSync(projectId)) return;
    // Warm the in-memory render cache first — synchronously, before the IDB
    // write. The synchronous prime is what removes the just-synced tile's
    // muted-placeholder flash; doing it before `putAsset` (not after) keeps
    // that working even if the IDB write fails — best-effort, see the catch.
    // Then persist to IndexedDB for durable offline rendering.
    primeObjectUrl(key, localBlob);
    await putAsset({
      key,
      blob: localBlob,
      contentType: localBlob.type || "application/octet-stream",
      fetchedAt: Date.now(),
      projectId,
      provisional: true,
    });
  } catch (err) {
    Sentry.captureException(err, {
      tags: {
        area: "offline-sync",
        phase: "seed-provisional-thumbnail",
        projectId,
      },
    });
  }
}

/**
 * Ensure every image referenced by a pinned project's currently-loaded
 * record/plan queries is cached. Idempotent and coalesced — a durably-cached
 * blob is skipped, a provisional one is upgraded, and the same image is never
 * downloaded twice concurrently.
 */
async function backfillProject(
  queryClient: QueryClient,
  projectId: string,
): Promise<void> {
  if (!isOnline() || !isPinnedSync(projectId)) return;

  const downloads: AssetDownload[] = [];
  for (const query of queryClient.getQueryCache().getAll()) {
    if (query.state.status !== "success" || query.state.data == null) continue;
    const key = query.queryKey;
    if (!Array.isArray(key) || key[1] !== projectId) continue;
    if (key[0] === "records") {
      downloads.push(
        ...recordDownloadsFrom(
          flattenInfiniteResults<ProjectRecord>(query.state.data),
        ),
      );
    } else if (key[0] === "plans" && key[2] === "list") {
      downloads.push(
        ...planDownloadsFrom(
          flattenInfiniteResults<ProjectPlan>(query.state.data),
        ),
      );
    }
  }

  const seen = new Set<string>();
  const unique = downloads.filter((d) => {
    if (seen.has(d.key)) return false;
    seen.add(d.key);
    return true;
  });

  const run = createSemaphore(BACKFILL_CONCURRENCY);
  await Promise.allSettled(
    unique.map((d) =>
      run(() =>
        ensureAssetCached(d.key, d.url, projectId).catch((err: unknown) => {
          Sentry.captureException(err, {
            tags: { area: "offline-sync", phase: "backfill", projectId },
          });
        }),
      ),
    ),
  );
}

/**
 * Register the asset-cache backfill subscriber on the QueryCache.
 *
 * Registered once for the app's lifetime (from `main.tsx`). Best-effort: every
 * path is wrapped so the subscriber callback can never throw and disrupt the
 * QueryCache's other listeners. Returns an unsubscribe function.
 */
export function registerAssetBackfill(queryClient: QueryClient): () => void {
  const timers = new Map<string, ReturnType<typeof setTimeout>>();

  const schedule = (projectId: string): void => {
    const existing = timers.get(projectId);
    if (existing) clearTimeout(existing);
    timers.set(
      projectId,
      setTimeout(() => {
        timers.delete(projectId);
        void backfillProject(queryClient, projectId);
      }, DEBOUNCE_MS),
    );
  };

  const consider = (query: Query): void => {
    try {
      const key = query.queryKey;
      if (!Array.isArray(key) || key.length < 2) return;
      const [root, projectId] = key;
      const matches =
        root === "records" || (root === "plans" && key[2] === "list");
      if (!matches || typeof projectId !== "string") return;
      if (query.state.status !== "success") return;
      if (!isPinnedSync(projectId)) return;
      schedule(projectId);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { area: "offline-sync", phase: "backfill-dispatch" },
      });
    }
  };

  // Registered before the persister restore (see `main.tsx`), so the cache is
  // empty here — hydrated queries arrive afterwards as `added` events.
  return queryClient.getQueryCache().subscribe((event) => {
    if (event.type === "added" || event.type === "updated") {
      consider(event.query);
    }
  });
}
